<template>
  <v-modal
    v-model="vModel"
    handle-click-bg
    full
    position="bottom"
    block-scroll
    animation="slide-up"
    min-width="50rem"
  >
    <template #header>
      {{ $t('components.ManagersList.title') }}
    </template>

    <div class="managers-list mx-auto my-1" style="max-width: 78rem" v-if="$apollo.queries.managersList.loading && !changingActivity">
      <div class="reset-indent">
        <list-item loading v-for="item in 5" :key="'loading-' + item"></list-item>
      </div>
    </div>

    <div class="managers-list mx-auto my-1" style="max-width: 78rem" v-else>
      <div class="reset-indent">
        <RecycleScroller
          :items="managers"
          :buffer="1000"
          key-field="id"
          :item-size="screen.breakPoint.hugeDesktop ? 72 : 65"
          page-mode
          v-slot="{ item }"
        >
            <list-item two-columns>
              <template v-slot:firstColumn>
                <list-item-group>
                  <list-item-content>
                    <list-item-title :style="!item.isActive ? 'opacity: .5' : null">
                      <span class="text--regular">@</span>{{ item.computedLogin }}
                      <span class="managers-list__activity d-inline-block" :class="!item.isActive ? 'managers-list__activity--inactive' : null"></span>
                    </list-item-title>

                    <list-item-subtitle :style="!item.isActive ? 'opacity: .5' : null">
                      <span>{{ item.computedName ? item.computedName : '—' }}</span>
                    </list-item-subtitle>
                  </list-item-content>
                </list-item-group>
              </template>

              <template v-slot:secondColumn>
                <list-item-title :style="!item.isActive ? 'opacity: .5' : null">
                  <span class="text--regular text--small">{{ item.email ? item.email : '—' }}</span>
                </list-item-title>
              </template>

              <template v-slot:controls v-if="!item.owner && ShowAddManagerControl && me && item.id.toString() !== me.id.toString()">
                <v-modal
                  :animation="!screen.breakPoint.tabPort ? 'slide-up' : 'slide-down'"
                  :width="!screen.breakPoint.tabPort ? '': 86"
                  :full="!screen.breakPoint.tabPort"
                  :noBackground="!screen.breakPoint.tabPort"
                  :max-body-height="screen.breakPoint.tabPort ? '70vh' : null"
                  block-scroll
                  theme="light-grey"
                  handle-click-bg
                  @input="(e) => handleAccessRights(e, item)"
                >
                  <template #activator="{ on }">
                    <kebab-menu>
                      <div class="d-flex justify-center pb-05">
                        <v-toggle :disabled="changingActivity" :id="item.id.toString()" :value="item.isActive" @input="(val) => toggleActivity(val, item)">
                          <template #before>
                            {{item.isActive ? $t('components.TerminalList.active') : $t('components.TerminalList.inactive') }}
                          </template>
                        </v-toggle>
                      </div>

                      <div class="divider my-1 reset-indent"></div>

                      <action-link :disabled="!item.isActive" class="pt-05" color="secondary" icon-left="icon-edit" @click="on" size="small">{{ $t('components.ManagersList.editRights') }}</action-link>
                    </kebab-menu>
                  </template>

                  <template #header>
                    {{ $t('components.ManagersList.editManagerRights') }}
                  </template>

                  <template #footer>
                    <div class="d-flex justify-end">
                      <v-button @click="save(item.id)" :loading="updating">{{ $t('widgets.Dashboard.EditWidget.save') }}</v-button>
                    </div>
                  </template>

                  <autocomplete class="mb-2" :placeholder="$t('components.ManagersList.rights.orderList')" v-model="accessRights.ORDER_LIST" value-field="value" item-text="name" :items="accessRightsValues" not-empty></autocomplete>
                  <autocomplete class="mb-2" :placeholder="$t('components.ManagersList.rights.bills')" v-model="accessRights.BILLS" value-field="value" item-text="name" :items="accessRightsValues" not-empty></autocomplete>
                  <autocomplete class="mb-2" :placeholder="$t('components.ManagersList.rights.managers')" v-model="accessRights.MANAGERS" value-field="value" item-text="name" :items="accessRightsValues" not-empty></autocomplete>
                  <autocomplete :placeholder="$t('components.ManagersList.rights.referrals')" v-model="accessRights.USERS" value-field="value" item-text="name" :items="accessRightsValues" not-empty></autocomplete>
                </v-modal>
              </template>
            </list-item>
        </RecycleScroller>
      </div>
    </div>

    <template #footer v-if="ShowAddManagerControl">
      <div class="d-flex justify-end mx-auto" style="max-width: 78rem">
        <v-modal
          :animation="!screen.breakPoint.tabPort ? 'slide-up' : 'slide-down'"
          :width="!screen.breakPoint.tabPort ? '': 86"
          :full="!screen.breakPoint.tabPort"
          :noBackground="!screen.breakPoint.tabPort"
          :max-body-height="screen.breakPoint.tabPort ? '70vh' : null"
          block-scroll
          theme="light-grey"
          handle-click-bg
          v-model="showAddModal"
        >
          <template #activator="{ on }">
            <v-button color="success" @click="on">
              <span class="d-flex align-center">
                <span class="mr-1 text--bold icon-plus d-block"></span>
                <span class="d-block">{{ $t('components.ManagersList.addManager') }}</span>
              </span>
            </v-button>
          </template>

          <template #header>
            {{ $t('components.ManagersList.addManager') }}
          </template>

          <v-input :placeholder="$t('components.ManagersList.newManager.login')" v-model="newManager.login" :error="newManagerError.login" class="mb-2"></v-input>
          <v-input :placeholder="$t('components.ManagersList.newManager.password')" v-model="newManager.password" :error="newManagerError.password" class="mb-2"></v-input>
          <v-input :placeholder="$t('components.ManagersList.newManager.name')" v-model="newManager.name" :error="newManagerError.name" class="mb-2"></v-input>
          <v-input :placeholder="$t('components.ManagersList.newManager.email')" v-model="newManager.email" :error="newManagerError.email" class="mb-2"></v-input>
          <v-input :placeholder="$t('components.ManagersList.newManager.phone')" v-model="newManager.phone" :error="newManagerError.phone"></v-input>

          <template #footer v-if="ShowAddManagerControl">
            <div class="d-flex justify-end">
              <v-button @click="addManager()" :loading="updating">{{ $t('components.ManagersList.addManager') }}</v-button>
            </div>
          </template>
        </v-modal>
      </div>
    </template>
  </v-modal>
</template>

<script>
import vModal from '@/ui/modals/vModal'
import vButton from '@/ui/controls/vButton'
import Autocomplete from '@/ui/controls/Autocomplete'
import vInput from '@/ui/controls/vInput'
import KebabMenu from '@/ui/controls/KebabMenu'
import vToggle from '@/ui/controls/vToggle'

import ListItem from '@/ui/slots/List/ListItem'
import ListItemGroup from '@/ui/slots/List/ListItemGroup'
import ListItemTitle from '@/ui/slots/List/ListItemTitle'
import ListItemContent from '@/ui/slots/List/ListItemContent'
import ListItemSubtitle from '@/ui/slots/List/ListItemSubtitle'

import ActionLink from '@/ui/controls/ActionLink'

import vModel from '@/mixins/vModel'
import screen from '@/mixins/screen'
import hasActiveContracts from '@/mixins/hasActiveContracts'
import { notifyGraphqlError } from '@/mixins/handleGraphqlError'

import { ManagerListGQL, MeGQL } from '@/graphql/queries'
import { UpdateManagerAccessGQL, AddManagerGQL, ChangeActiveManager } from '@/graphql/mutations'
import { ShowAddManagerControl } from '@/mixins/displayRules'

export default {
  mixins: [vModel, screen, hasActiveContracts, notifyGraphqlError, ShowAddManagerControl],

  data () {
    return {
      showAddModal: false,
      updating: false,
      changingActivity: false,

      newManager: {
        login: null,
        password: null,
        name: null,
        email: null,
        phone: null
      },

      newManagerError: {
        login: null,
        password: null,
        name: null,
        email: null,
        phone: null
      },

      accessRights: {
        ORDER_LIST: 'CLOSED',
        BILLS: 'CLOSED',
        USERS: 'CLOSED',
        MANAGERS: 'CLOSED'
      }
    }
  },

  apollo: {
    managersList: {
      query: ManagerListGQL,
      update: data => data.merchant.managerList,
      skip () { return !this.hasActiveContracts && !this.vModel }
    },

    me: {
      query: MeGQL
    }
  },

  computed: {
    accessRightsValues () {
      return [
        {
          name: this.$i18n.t('components.ManagersList.right.read'),
          value: 'READ'
        },
        {
          name: this.$i18n.t('components.ManagersList.right.write'),
          value: 'WRITE'
        },
        {
          name: this.$i18n.t('components.ManagersList.right.closed'),
          value: 'CLOSED'
        }
      ]
    },

    managers () {
      if (!this.managersList) {
        return []
      }

      const cutNameLength = this.screen.breakPoint.tabPort ? 40 : 20
      const cutLoginLength = this.screen.breakPoint.tabPort ? 34 : 17

      return this.managersList.map(m => {
        m.computedName = m.name && m.name.length > cutNameLength ? m.name.substr(0, cutNameLength - 3) + '...' : m.name
        m.computedLogin = m.login && m.login.length > cutLoginLength ? m.login.substr(0, cutLoginLength - 3) + '...' : m.login

        return m
      }).sort((a, b) => a.id - b.id)
    }
  },

  methods: {
    async toggleActivity (val, item) {
      this.changingActivity = true
      try {
        await this.$apollo.mutate({
          mutation: ChangeActiveManager,
          variables: {
            id: parseInt(item.id)
          }
        })
        await this.$apollo.queries.managersList.refetch()
        this.changingActivity = false
      } catch (e) {
        this.notifyGraphqlError(e)
        this.changingActivity = false
      }
    },

    handleAccessRights (e, item) {
      if (e) {
        item.managerAccess.partnerAccessList.forEach((item) => {
          this.accessRights[item.role] = item.access
        })
      }
    },

    validateNewManager () {
      const formData = this.newManager

      for (const key of Object.keys(formData)) {
        if (!formData[key]) {
          this.errors[key] = 'errors.Components.AddOrder.validation.mustNotBeEmpty'
        }
      }

      return !Object.keys(this.newManagerError).some(key => this.newManagerError[key] && true)
    },

    async addManager () {
      if (!this.validateNewManager) {
        return
      }

      this.updating = true

      try {
        await this.$apollo.mutate({
          mutation: AddManagerGQL,
          variables: {
            params: this.newManager
          }
        })

        this.updating = false
        this.showAddModal = false

        await this.$apollo.queries.managersList.refetch()

        this.newManager = {
          login: null,
          password: null,
          name: null,
          email: null,
          phone: null
        }

        this.$notify({
          type: 'success',
          message: this.$i18n.t('components.ManagersList.newManagerAdded'),
          timeout: 3000
        })
      } catch (e) {
        this.updating = false
        this.notifyGraphqlError(e)
      }
    },

    async save (id) {
      const params = {
        partnerManagerId: id,
        partnerAccessList: Object.keys(this.accessRights).map(key => {
          return {
            role: key,
            access: this.accessRights[key]
          }
        })
      }

      this.updating = true

      try {
        await this.$apollo.mutate({
          mutation: UpdateManagerAccessGQL,
          variables: {
            params
          },
          update: (store, { data: { updateManagerAccess } }) => {
            const data = store.readQuery({ query: ManagerListGQL })

            const index = data.merchant.managerList.findIndex(m => m.id.toString() === updateManagerAccess.partnerManagerId.toString())
            if (index > -1) {
              data.merchant.managerList[index].managerAccess.partnerAccessList = updateManagerAccess.partnerAccessList
            }

            store.writeQuery({ query: ManagerListGQL, data })
          }
        })

        // await this.$apollo.queries.managersList.refetch()

        this.updating = false

        this.$notify({
          type: 'success',
          message: this.$i18n.t('components.ManagersList.rights.updated'),
          delay: 150,
          timeout: 3000
        })
      } catch (e) {
        this.updating = false
        this.notifyGraphqlError(e)
      }
    }
  },

  props: {
    loading: {
      type: Boolean,
      default: false
    }
    //
    // managers: {
    //   type: Array,
    //   default: () => []
    // }
  },

  components: {
    vModal,
    vButton,
    vInput,
    Autocomplete,
    KebabMenu,
    vToggle,

    ListItem,
    ListItemGroup,
    ListItemTitle,
    ListItemContent,
    ListItemSubtitle,

    ActionLink
  }
}
</script>

<style lang="scss">
  .managers-list {
    &__activity {
      width: 1rem;
      height: 1rem;
      border-radius: 50%;
      background-color: $color-success;

      &--inactive {
        background-color: $color-error;
      }
    }
  }
</style>
